import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@ohif/ui-next';
import { Clock, X } from 'lucide-react';

import { ToggleSwitch } from '../common';
import { API_ENDPOINTS } from '../../config/api.config';
import { apiGet } from '../../utils/apiClient';
import { type DicomRoutingRule } from '../../services/dicomRoutingRuleService';
import {
  fetchAndStoreModalities,
  getModalities,
  getRoutingRuleModalityOptions,
  modalityCodesMatch,
  type ModalityOption,
} from '../../utils/modalityUtils';
import {
  createStudyAssignmentRule,
  updateStudyAssignmentRule,
  type CreateStudyAssignmentRuleRequest,
  type StudyAssignmentRule,
  type StudyAssignmentType,
} from '../../services/studyAssignmentRuleService';
import {
  getRadiologyTestNames,
  type RadiologyTestNameOption,
} from '../../services/radiologyTestService';
import { ICON_STROKE_WIDTH, Z_INDEX_CHANGE_PASSWORD_MODAL } from '../../constants';
import { SPECIALTY_OPTIONS } from '../../constants/constant';
import RadiologyTestNamePicker from './RadiologyTestNamePicker';
import { parseStoredTestNames, serializeStoredTestNames } from '../../utils/testNameListUtils';
import {
  findStudyAssignmentModalityConflict,
  hasPrefetchedCrossRuleValidationData,
  shouldRunModalityConflictCheck,
} from '../../utils/risRuleModalityConflictUtils';
import './AddRoutingRuleModal.css';
import './AddStudyAssignmentRuleModal.css';

interface AddStudyAssignmentRuleFormValues {
  name: string;
  modalityIds: number[];
  selectedTestNames: string[];
  requiredExpertise: string;
  matchPriority: string;
  assignmentType: StudyAssignmentType;
  radiologistId: number | null;
  radiologistIds: number[];
  onCallStartHour: string;
  onCallEndHour: string;
  active: boolean;
}

interface RadiologistOption {
  id: number;
  full_name: string;
  specialty?: string[] | string | null;
  reporting_modality_access?: string[] | string | null;
}

function normalizeModalityAccessList(value?: string[] | string | null): string[] {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map(item => String(item).trim()).filter(Boolean);
  }
  return [String(value).trim()].filter(Boolean);
}

function radiologistMatchesModalityAccess(
  rad: RadiologistOption,
  selectedModalityCodes: string[]
): boolean {
  if (!selectedModalityCodes.length) {
    return true;
  }
  const access = normalizeModalityAccessList(rad.reporting_modality_access);
  if (!access.length) {
    return false;
  }
  return selectedModalityCodes.some(selected =>
    access.some(item => modalityCodesMatch(selected, item))
  );
}

function radiologistMatchesFilters(
  rad: RadiologistOption,
  specialty: string,
  selectedModalityCodes: string[],
  allSpecialties = false
): boolean {
  const modalityOk = radiologistMatchesModalityAccess(rad, selectedModalityCodes);
  if (allSpecialties || !specialty.trim()) {
    return modalityOk;
  }
  return radiologistMatchesSpecialty(rad, specialty) && modalityOk;
}

function normalizeSpecialtyList(value?: string[] | string | null): string[] {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map(item => String(item).trim()).filter(Boolean);
  }
  return String(value)
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
}

function radiologistMatchesSpecialty(rad: RadiologistOption, specialty: string): boolean {
  if (!specialty.trim()) {
    return true;
  }
  const needle = specialty.trim().toLowerCase();
  return normalizeSpecialtyList(rad.specialty).some(item => {
    const hay = item.toLowerCase();
    return hay === needle || hay.includes(needle);
  });
}

interface AddStudyAssignmentRuleModalProps {
  isOpen: boolean;
  facilityId: number;
  editingRule?: StudyAssignmentRule | null;
  /** Prefetched on the study assignment page; null skips frontend validation. */
  activeRoutingRules?: DicomRoutingRule[] | null;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}

const MATCH_PRIORITY_OPTIONS = [
  { value: '', label: 'Any priority' },
  { value: 'ROUTINE', label: 'ROUTINE' },
  { value: 'URGENT', label: 'URGENT' },
  { value: 'VIP', label: 'VIP' },
  { value: 'MLC', label: 'MLC' },
];

const ASSIGNMENT_MODALITY_LABELS: Record<string, string> = {
  MR: 'MRI',
  MRI: 'MRI',
  CT: 'CT',
  XR: 'XRAY',
  XRAY: 'XRAY',
  US: 'US',
  USG: 'US',
};

const ASSIGN_TO_OPTIONS: { value: StudyAssignmentType; label: string }[] = [
  { value: 'specific', label: 'Specific radiologist' },
  { value: 'least_loaded', label: 'Least-loaded (auto-pick)' },
  { value: 'group', label: 'Group' },
];

const ALL_SPECIALTIES_VALUE = '__ALL__';

const DEFAULT_FORM: AddStudyAssignmentRuleFormValues = {
  name: '',
  modalityIds: [],
  selectedTestNames: [],
  requiredExpertise: '',
  matchPriority: '',
  assignmentType: 'specific',
  radiologistId: null,
  radiologistIds: [],
  onCallStartHour: '',
  onCallEndHour: '',
  active: true,
};

function ruleToForm(rule: StudyAssignmentRule): AddStudyAssignmentRuleFormValues {
  return {
    name: rule.name,
    modalityIds: rule.modalityIds ?? [],
    selectedTestNames: parseStoredTestNames(rule.matchSubspecialty),
    requiredExpertise: rule.requiredExpertise ?? '',
    matchPriority: rule.matchPriority === 'STAT' ? 'URGENT' : (rule.matchPriority ?? ''),
    assignmentType: rule.assignmentType ?? 'specific',
    radiologistId: rule.radiologistId,
    radiologistIds: rule.radiologistIds ?? [],
    onCallStartHour: rule.onCallStartHour == null ? '' : String(rule.onCallStartHour),
    onCallEndHour: rule.onCallEndHour == null ? '' : String(rule.onCallEndHour),
    active: rule.isActive,
  };
}

function formatAssignmentModalityLabel(option: ModalityOption): string {
  const key = option.value.trim().toUpperCase();
  return ASSIGNMENT_MODALITY_LABELS[key] ?? option.label;
}

function clampHourValue(raw: string): string {
  if (raw.trim() === '') {
    return '';
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) {
    return '';
  }
  return String(Math.min(24, Math.max(0, Math.trunc(parsed))));
}

function SpecialtyTriggerLabel({
  requiredExpertise,
  specialtyIsAll,
}: Readonly<{
  requiredExpertise: string;
  specialtyIsAll: boolean;
}>) {
  if (requiredExpertise) {
    return <span className="add-routing-rule-study-summary">{requiredExpertise}</span>;
  }
  if (specialtyIsAll) {
    return <span className="add-routing-rule-study-summary">All specialties</span>;
  }
  return <span className="add-routing-rule-placeholder">Select specialty…</span>;
}

function AssignmentSpecialtyField({
  specialtyDropdownRef,
  specialtyDropdownOpen,
  onToggleOpen,
  requiredExpertise,
  specialtyIsAll,
  specialtySearchQuery,
  onSpecialtySearchChange,
  showAllSpecialtiesOption,
  filteredSpecialtyOptions,
  onSpecialtySelect,
}: Readonly<{
  specialtyDropdownRef: React.RefObject<HTMLDivElement>;
  specialtyDropdownOpen: boolean;
  onToggleOpen: () => void;
  requiredExpertise: string;
  specialtyIsAll: boolean;
  specialtySearchQuery: string;
  onSpecialtySearchChange: (value: string) => void;
  showAllSpecialtiesOption: boolean;
  filteredSpecialtyOptions: Array<{ value: string; label: string }>;
  onSpecialtySelect: (value: string) => void;
}>) {
  return (
    <div
      className="add-routing-rule-field"
      ref={specialtyDropdownRef}
    >
      <label
        className="add-routing-rule-label"
        htmlFor="assignment-rule-specialty"
      >
        Specialty
      </label>
      <div className="add-routing-rule-study-types">
        <button
          id="assignment-rule-specialty"
          type="button"
          className={['add-routing-rule-study-trigger', specialtyDropdownOpen ? 'open' : '']
            .filter(Boolean)
            .join(' ')}
          onClick={onToggleOpen}
          aria-expanded={specialtyDropdownOpen}
          aria-haspopup="listbox"
        >
          <SpecialtyTriggerLabel
            requiredExpertise={requiredExpertise}
            specialtyIsAll={specialtyIsAll}
          />
        </button>
        {specialtyDropdownOpen ? (
          <div className="add-routing-rule-study-dropdown">
            <input
              type="text"
              className="add-routing-rule-study-search"
              placeholder="Enter text here…"
              value={specialtySearchQuery}
              onChange={e => onSpecialtySearchChange(e.target.value)}
              onMouseDown={e => e.stopPropagation()}
            />
            <div className="add-routing-rule-study-options">
              {showAllSpecialtiesOption ? (
                <button
                  type="button"
                  className={['add-routing-rule-study-option', specialtyIsAll ? 'selected' : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => onSpecialtySelect(ALL_SPECIALTIES_VALUE)}
                >
                  <span className="add-routing-rule-study-option-name">All specialties</span>
                </button>
              ) : null}
              {filteredSpecialtyOptions.length === 0 ? (
                <div className="add-routing-rule-study-option muted">No specialties found</div>
              ) : (
                filteredSpecialtyOptions.map(option => {
                  const selected =
                    !specialtyIsAll &&
                    requiredExpertise.trim().toLowerCase() === option.value.toLowerCase();
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={['add-routing-rule-study-option', selected ? 'selected' : '']
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => onSpecialtySelect(option.value)}
                    >
                      <span className="add-routing-rule-study-option-name">{option.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
      <p className="add-routing-rule-hint">
        Choose All specialties to see every radiologist, or pick one to filter the list below.
      </p>
    </div>
  );
}

function RadiologistPickerOptions({
  filteredRadiologists,
  emptyMessage,
  isSelected,
  onSelect,
}: Readonly<{
  filteredRadiologists: RadiologistOption[];
  emptyMessage: string;
  isSelected: (id: number) => boolean;
  onSelect: (id: number) => void;
}>) {
  if (filteredRadiologists.length === 0) {
    return <div className="add-routing-rule-study-option muted">{emptyMessage}</div>;
  }
  return (
    <>
      {filteredRadiologists.map(rad => {
        const selected = isSelected(rad.id);
        return (
          <button
            key={rad.id}
            type="button"
            className={['add-routing-rule-study-option', selected ? 'selected' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onSelect(rad.id)}
          >
            <span className="add-routing-rule-study-option-name">{rad.full_name}</span>
          </button>
        );
      })}
    </>
  );
}

function AssignmentRadiologistPickerField({
  fieldId,
  label,
  pickerRef,
  pickerOpen,
  onToggleOpen,
  disabled,
  summaryText,
  emptyMessage,
  searchValue,
  onSearchChange,
  filteredRadiologists,
  isSelected,
  onSelect,
  onDone,
  hint,
}: Readonly<{
  fieldId: string;
  label: string;
  pickerRef: React.RefObject<HTMLDivElement>;
  pickerOpen: boolean;
  onToggleOpen: () => void;
  disabled: boolean;
  summaryText: string | null;
  emptyMessage: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filteredRadiologists: RadiologistOption[];
  isSelected: (id: number) => boolean;
  onSelect: (id: number) => void;
  onDone: () => void;
  hint?: string;
}>) {
  return (
    <div className="add-routing-rule-field">
      <label
        className="add-routing-rule-label"
        htmlFor={fieldId}
      >
        {label}
      </label>
      <div
        className="add-routing-rule-study-types"
        ref={pickerRef}
      >
        <button
          id={fieldId}
          type="button"
          className={['add-routing-rule-study-trigger', pickerOpen ? 'open' : '']
            .filter(Boolean)
            .join(' ')}
          onClick={onToggleOpen}
          disabled={disabled}
          aria-expanded={pickerOpen}
          aria-haspopup="listbox"
        >
          {summaryText ? (
            <span className="add-routing-rule-study-summary">{summaryText}</span>
          ) : (
            <span className="add-routing-rule-placeholder">{emptyMessage}</span>
          )}
        </button>
        {pickerOpen ? (
          <div className="add-routing-rule-study-dropdown">
            <input
              type="text"
              className="add-routing-rule-study-search"
              placeholder="Search radiologists"
              value={searchValue}
              onChange={e => onSearchChange(e.target.value)}
            />
            <div className="add-routing-rule-study-options">
              <RadiologistPickerOptions
                filteredRadiologists={filteredRadiologists}
                emptyMessage={emptyMessage}
                isSelected={isSelected}
                onSelect={onSelect}
              />
            </div>
            <div className="add-assignment-rule-picker-footer">
              <button
                type="button"
                className="add-assignment-rule-picker-done"
                onClick={onDone}
              >
                Done
              </button>
            </div>
          </div>
        ) : null}
      </div>
      {hint ? <p className="add-routing-rule-hint">{hint}</p> : null}
    </div>
  );
}

function AssignmentModalityPills({
  loadingModalities,
  modalityOptions,
  selectedModalityIds,
  onToggle,
}: Readonly<{
  loadingModalities: boolean;
  modalityOptions: ModalityOption[];
  selectedModalityIds: number[];
  onToggle: (id: number) => void;
}>) {
  if (loadingModalities) {
    return <span className="add-routing-rule-muted">Loading modalities…</span>;
  }
  return (
    <>
      {modalityOptions.map(option => {
        const selected = selectedModalityIds.includes(option.id);
        return (
          <button
            key={option.id}
            type="button"
            className={['add-routing-rule-modality-pill', selected ? 'selected' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onToggle(option.id)}
          >
            {formatAssignmentModalityLabel(option)}
          </button>
        );
      })}
    </>
  );
}

function AssignmentTargetFields({
  assignmentType,
  radiologistPickerRef,
  radiologistPickerOpen,
  onToggleRadiologistPicker,
  radiologistPickerDisabled,
  selectedSpecificRadiologistName,
  selectedGroupRadiologistNames,
  radiologistEmptyMessage,
  radiologistSearch,
  onRadiologistSearchChange,
  filteredRadiologists,
  selectedRadiologistId,
  selectedRadiologistIds,
  onSpecificSelect,
  onGroupToggle,
  onDone,
}: Readonly<{
  assignmentType: StudyAssignmentType;
  radiologistPickerRef: React.RefObject<HTMLDivElement>;
  radiologistPickerOpen: boolean;
  onToggleRadiologistPicker: () => void;
  radiologistPickerDisabled: boolean;
  selectedSpecificRadiologistName: string;
  selectedGroupRadiologistNames: string[];
  radiologistEmptyMessage: string;
  radiologistSearch: string;
  onRadiologistSearchChange: (value: string) => void;
  filteredRadiologists: RadiologistOption[];
  selectedRadiologistId: number | null;
  selectedRadiologistIds: number[];
  onSpecificSelect: (id: number) => void;
  onGroupToggle: (id: number) => void;
  onDone: () => void;
}>) {
  if (assignmentType === 'specific') {
    return (
      <AssignmentRadiologistPickerField
        fieldId="assignment-rule-radiologist"
        label="Radiologist"
        pickerRef={radiologistPickerRef}
        pickerOpen={radiologistPickerOpen}
        onToggleOpen={onToggleRadiologistPicker}
        disabled={radiologistPickerDisabled}
        summaryText={selectedSpecificRadiologistName || null}
        emptyMessage={radiologistEmptyMessage}
        searchValue={radiologistSearch}
        onSearchChange={onRadiologistSearchChange}
        filteredRadiologists={filteredRadiologists}
        isSelected={id => selectedRadiologistId === id}
        onSelect={onSpecificSelect}
        onDone={onDone}
      />
    );
  }
  if (assignmentType === 'group') {
    return (
      <AssignmentRadiologistPickerField
        fieldId="assignment-rule-group"
        label="Radiologists"
        pickerRef={radiologistPickerRef}
        pickerOpen={radiologistPickerOpen}
        onToggleOpen={onToggleRadiologistPicker}
        disabled={radiologistPickerDisabled}
        summaryText={
          selectedGroupRadiologistNames.length > 0 ? selectedGroupRadiologistNames.join(', ') : null
        }
        emptyMessage={radiologistEmptyMessage}
        searchValue={radiologistSearch}
        onSearchChange={onRadiologistSearchChange}
        filteredRadiologists={filteredRadiologists}
        isSelected={id => selectedRadiologistIds.includes(id)}
        onSelect={onGroupToggle}
        onDone={onDone}
        hint="Pick two or more radiologists. The least-loaded eligible doctor is auto-assigned when a study matches."
      />
    );
  }
  if (assignmentType === 'least_loaded') {
    return (
      <div className="add-routing-rule-field">
        <span className="add-routing-rule-label">Radiologist pool</span>
        <p className="add-routing-rule-hint add-assignment-rule-least-loaded-hint">
          Active facility radiologists who match modality access and required expertise are
          considered; the one with the fewest assigned studies is picked automatically.
        </p>
      </div>
    );
  }
  return null;
}

const AddStudyAssignmentRuleModal: React.FC<AddStudyAssignmentRuleModalProps> = ({
  isOpen,
  facilityId,
  editingRule,
  activeRoutingRules = null,
  onClose,
  onSubmit,
}) => {
  const isEdit = Boolean(editingRule);
  const [form, setForm] = useState<AddStudyAssignmentRuleFormValues>(DEFAULT_FORM);
  const [modalityOptions, setModalityOptions] = useState<ModalityOption[]>([]);
  const [radiologists, setRadiologists] = useState<RadiologistOption[]>([]);
  const [loadingModalities, setLoadingModalities] = useState(false);
  const [loadingRadiologists, setLoadingRadiologists] = useState(false);
  const [radiologyTests, setRadiologyTests] = useState<RadiologyTestNameOption[]>([]);
  const [loadingRadiologyTests, setLoadingRadiologyTests] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [radiologistPickerOpen, setRadiologistPickerOpen] = useState(false);
  const [radiologistSearch, setRadiologistSearch] = useState('');
  const [specialtyDropdownOpen, setSpecialtyDropdownOpen] = useState(false);
  const [specialtySearchQuery, setSpecialtySearchQuery] = useState('');
  const [specialtyIsAll, setSpecialtyIsAll] = useState(false);
  const radiologistPickerRef = useRef<HTMLDivElement>(null);
  const specialtyDropdownRef = useRef<HTMLDivElement>(null);

  const loadModalities = useCallback(async () => {
    setLoadingModalities(true);
    try {
      let stored = getModalities();
      if (!stored?.length) {
        await fetchAndStoreModalities();
        stored = getModalities();
      }
      setModalityOptions(getRoutingRuleModalityOptions(stored));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load modalities');
    } finally {
      setLoadingModalities(false);
    }
  }, []);

  const loadRadiologists = useCallback(async () => {
    setLoadingRadiologists(true);
    try {
      const url = API_ENDPOINTS.RADIOLOGISTS.GET({
        role: 'Radiologist',
        status: 'Active',
        facility_id: facilityId,
        limit: 200,
      });
      const response = await apiGet<{
        success?: boolean;
        data?: RadiologistOption[];
      }>(url);
      const body = response.data;
      const users = (Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : []).map(
        user => ({
          id: Number(user.id),
          full_name: String(user.full_name ?? user.fullName ?? ''),
          specialty: user.specialty ?? null,
          reporting_modality_access:
            user.reporting_modality_access ?? user.reportingModalityAccess ?? null,
        })
      );
      setRadiologists(users.filter(user => user.id && user.full_name));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load radiologists');
    } finally {
      setLoadingRadiologists(false);
    }
  }, [facilityId]);

  const loadRadiologyTests = useCallback(async () => {
    setLoadingRadiologyTests(true);
    try {
      const result = await getRadiologyTestNames(facilityId, { active_only: true });
      if (!result.success) {
        setLoadError(result.error || 'Failed to load radiology tests');
        setRadiologyTests([]);
        return;
      }
      setRadiologyTests(result.data ?? []);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load radiology tests');
      setRadiologyTests([]);
    } finally {
      setLoadingRadiologyTests(false);
    }
  }, [facilityId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setLoadError(null);
    setSubmitError(null);
    setRadiologistPickerOpen(false);
    setRadiologistSearch('');
    setSpecialtyDropdownOpen(false);
    setSpecialtySearchQuery('');
    const nextForm = editingRule ? ruleToForm(editingRule) : DEFAULT_FORM;
    setForm(nextForm);
    setSpecialtyIsAll(editingRule ? !editingRule.requiredExpertise?.trim() : false);
    loadModalities();
    loadRadiologists();
    loadRadiologyTests();
  }, [isOpen, editingRule, loadModalities, loadRadiologists, loadRadiologyTests]);

  useEffect(() => {
    if (!radiologistPickerOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (
        radiologistPickerRef.current &&
        !radiologistPickerRef.current.contains(event.target as Node)
      ) {
        setRadiologistPickerOpen(false);
        setRadiologistSearch('');
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setRadiologistPickerOpen(false);
        setRadiologistSearch('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [radiologistPickerOpen]);

  useEffect(() => {
    if (!specialtyDropdownOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (
        specialtyDropdownRef.current &&
        !specialtyDropdownRef.current.contains(event.target as Node)
      ) {
        setSpecialtyDropdownOpen(false);
        setSpecialtySearchQuery('');
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSpecialtyDropdownOpen(false);
        setSpecialtySearchQuery('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [specialtyDropdownOpen]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }
    const rafId = requestAnimationFrame(() => {
      const content = document.querySelector(
        '.add-routing-rule-dialog-content'
      ) as HTMLElement | null;
      if (content) {
        content.style.setProperty('z-index', String(Z_INDEX_CHANGE_PASSWORD_MODAL), 'important');
        const overlay = content.previousElementSibling as HTMLElement | null;
        if (overlay) {
          overlay.style.setProperty('z-index', String(Z_INDEX_CHANGE_PASSWORD_MODAL), 'important');
        }
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [isOpen]);

  const updateForm = <K extends keyof AddStudyAssignmentRuleFormValues>(
    key: K,
    value: AddStudyAssignmentRuleFormValues[K]
  ) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const toggleModality = (modalityId: number) => {
    setForm(prev => {
      const nextModalityIds = prev.modalityIds.includes(modalityId)
        ? prev.modalityIds.filter(id => id !== modalityId)
        : [...prev.modalityIds, modalityId];
      const codes = modalityOptions
        .filter(opt => nextModalityIds.includes(opt.id))
        .map(opt => opt.value);
      const matchingIds = new Set(
        radiologists
          .filter(rad =>
            radiologistMatchesFilters(rad, prev.requiredExpertise, codes, specialtyIsAll)
          )
          .map(rad => rad.id)
      );

      return {
        ...prev,
        modalityIds: nextModalityIds,
        radiologistId:
          prev.radiologistId && matchingIds.has(prev.radiologistId) ? prev.radiologistId : null,
        radiologistIds: prev.radiologistIds.filter(id => matchingIds.has(id)),
      };
    });
  };

  const selectedModalityCodes = useMemo(
    () =>
      modalityOptions
        .filter(option => form.modalityIds.includes(option.id))
        .map(option => option.value),
    [modalityOptions, form.modalityIds]
  );

  const filteredRadiologyTests = useMemo(() => {
    if (!selectedModalityCodes.length) {
      return radiologyTests;
    }
    return radiologyTests.filter(test =>
      selectedModalityCodes.some(code => modalityCodesMatch(code, test.modality ?? ''))
    );
  }, [radiologyTests, selectedModalityCodes]);

  useEffect(() => {
    if (!isOpen || loadingRadiologyTests) {
      return;
    }

    const validNames = new Set(filteredRadiologyTests.map(test => test.test_name));
    setForm(prev => {
      const nextSelected = prev.selectedTestNames.filter(name => validNames.has(name));
      if (nextSelected.length === prev.selectedTestNames.length) {
        return prev;
      }
      return { ...prev, selectedTestNames: nextSelected };
    });
  }, [isOpen, loadingRadiologyTests, filteredRadiologyTests]);

  const pruneRadiologistSelection = (
    prev: AddStudyAssignmentRuleFormValues,
    specialty: string,
    modalityCodes: string[],
    allSpecialties = false
  ) => {
    const matchingIds = new Set(
      radiologists
        .filter(rad => radiologistMatchesFilters(rad, specialty, modalityCodes, allSpecialties))
        .map(rad => rad.id)
    );

    return {
      ...prev,
      requiredExpertise: allSpecialties ? '' : specialty,
      radiologistId:
        prev.radiologistId && matchingIds.has(prev.radiologistId) ? prev.radiologistId : null,
      radiologistIds: prev.radiologistIds.filter(id => matchingIds.has(id)),
    };
  };

  const hasCriterion = useMemo(() => {
    return Boolean(
      form.modalityIds.length ||
        form.selectedTestNames.length ||
        specialtyIsAll ||
        form.requiredExpertise.trim() ||
        form.matchPriority.trim()
    );
  }, [form, specialtyIsAll]);

  const toggleGroupRadiologist = (radiologistId: number) => {
    setForm(prev => ({
      ...prev,
      radiologistIds: prev.radiologistIds.includes(radiologistId)
        ? prev.radiologistIds.filter(id => id !== radiologistId)
        : [...prev.radiologistIds, radiologistId],
    }));
  };

  const handleSpecialtySelect = (specialty: string) => {
    if (specialty === ALL_SPECIALTIES_VALUE) {
      setSpecialtyIsAll(true);
      setForm(prev => pruneRadiologistSelection(prev, '', selectedModalityCodes, true));
    } else {
      setSpecialtyIsAll(false);
      setForm(prev => pruneRadiologistSelection(prev, specialty, selectedModalityCodes));
    }
    setSpecialtyDropdownOpen(false);
    setSpecialtySearchQuery('');
    setRadiologistPickerOpen(false);
  };

  const eligibleRadiologists = useMemo(() => {
    return radiologists.filter(rad =>
      radiologistMatchesFilters(rad, form.requiredExpertise, selectedModalityCodes, specialtyIsAll)
    );
  }, [radiologists, form.requiredExpertise, selectedModalityCodes, specialtyIsAll]);

  const radiologistPickerDisabled =
    loadingRadiologists ||
    (!specialtyIsAll && !form.requiredExpertise.trim()) ||
    eligibleRadiologists.length === 0;

  const radiologistEmptyMessage = useMemo(() => {
    if (loadingRadiologists) {
      return 'Loading radiologists…';
    }
    if (!specialtyIsAll && !form.requiredExpertise.trim()) {
      return 'Select a specialty or choose All specialties';
    }
    if (eligibleRadiologists.length === 0) {
      return selectedModalityCodes.length
        ? 'No radiologists match the selected filters and modality access'
        : specialtyIsAll
          ? 'No radiologists available'
          : 'No radiologists with this specialty';
    }
    return form.assignmentType === 'group' ? 'Select radiologists…' : 'Select radiologist…';
  }, [
    loadingRadiologists,
    form.requiredExpertise,
    specialtyIsAll,
    eligibleRadiologists.length,
    selectedModalityCodes.length,
    form.assignmentType,
  ]);

  const selectedSpecificRadiologistName = useMemo(() => {
    if (!form.radiologistId) {
      return '';
    }
    return (
      eligibleRadiologists.find(rad => rad.id === form.radiologistId)?.full_name ??
      radiologists.find(rad => rad.id === form.radiologistId)?.full_name ??
      ''
    );
  }, [form.radiologistId, eligibleRadiologists, radiologists]);

  const filteredSpecialtyOptions = useMemo(() => {
    const search = specialtySearchQuery.trim().toLowerCase();
    if (!search) {
      return SPECIALTY_OPTIONS;
    }
    return SPECIALTY_OPTIONS.filter(option => option.label.toLowerCase().includes(search));
  }, [specialtySearchQuery]);

  const showAllSpecialtiesOption = useMemo(() => {
    const search = specialtySearchQuery.trim().toLowerCase();
    return !search || 'all specialties'.includes(search);
  }, [specialtySearchQuery]);

  const filteredRadiologists = useMemo(() => {
    const search = radiologistSearch.trim().toLowerCase();
    if (!search) {
      return eligibleRadiologists;
    }
    return eligibleRadiologists.filter(rad => rad.full_name.toLowerCase().includes(search));
  }, [eligibleRadiologists, radiologistSearch]);

  const selectedGroupRadiologistNames = useMemo(() => {
    const selected = new Set(form.radiologistIds);
    return eligibleRadiologists.filter(rad => selected.has(rad.id)).map(rad => rad.full_name);
  }, [form.radiologistIds, eligibleRadiologists]);

  const handleSpecificRadiologistSelect = (radiologistId: number) => {
    updateForm('radiologistId', radiologistId);
    setRadiologistPickerOpen(false);
    setRadiologistSearch('');
  };

  const buildPayload = (): CreateStudyAssignmentRuleRequest => ({
    facilityId,
    name: form.name.trim(),
    modalityIds: form.modalityIds.length ? form.modalityIds : undefined,
    matchSubspecialty: serializeStoredTestNames(form.selectedTestNames) || null,
    // "All specialties" intentionally sends null so the backend matches any expertise
    requiredExpertise: specialtyIsAll ? null : form.requiredExpertise.trim() || null,
    matchPriority: form.matchPriority.trim() || null,
    assignmentType: form.assignmentType,
    radiologistId: form.assignmentType === 'specific' ? form.radiologistId : null,
    radiologistIds: form.assignmentType === 'group' ? form.radiologistIds : undefined,
    onCallStartHour: form.onCallStartHour.trim() === '' ? null : Number(form.onCallStartHour),
    onCallEndHour: form.onCallEndHour.trim() === '' ? null : Number(form.onCallEndHour),
    isActive: form.active,
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!form.name.trim()) {
      setSubmitError('Rule name is required');
      return;
    }
    // When specialtyIsAll is true, requiredExpertise is intentionally omitted (null in
    // buildPayload). Only require a concrete specialty when "All specialties" is not selected.
    if (!specialtyIsAll && !form.requiredExpertise.trim()) {
      setSubmitError('Select a specialty or choose All specialties');
      return;
    }
    if (!hasCriterion) {
      setSubmitError('Configure at least one match criterion');
      return;
    }
    if (form.assignmentType === 'specific' && !form.radiologistId) {
      setSubmitError('Select a radiologist');
      return;
    }
    if (form.assignmentType === 'group' && form.radiologistIds.length < 2) {
      setSubmitError('Select at least two radiologists for group assignment');
      return;
    }

    if (
      hasPrefetchedCrossRuleValidationData(activeRoutingRules) &&
      shouldRunModalityConflictCheck(isEdit, editingRule, {
        modalityIds: form.modalityIds,
        active: form.active,
      })
    ) {
      const modalityConflict = findStudyAssignmentModalityConflict(
        form.modalityIds,
        activeRoutingRules,
        modalityOptions,
        form.active
      );
      if (modalityConflict) {
        setSubmitError(modalityConflict);
        return;
      }
    }

    setIsSubmitting(true);
    const payload = buildPayload();
    const result = isEdit
      ? await updateStudyAssignmentRule(editingRule!.id, payload)
      : await createStudyAssignmentRule(payload);

    setIsSubmitting(false);

    if (!result.success) {
      setSubmitError(result.error || 'Failed to save rule');
      return;
    }

    await onSubmit();
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const closeRadiologistPicker = () => {
    setRadiologistPickerOpen(false);
    setRadiologistSearch('');
  };

  const toggleRadiologistPicker = () => {
    setRadiologistPickerOpen(open => !open);
    setSpecialtyDropdownOpen(false);
  };

  const renderAddStudyAssignmentRuleModal = () => (
    <Dialog
      open={isOpen}
      onOpenChange={open => !open && !isSubmitting && onClose()}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
    >
      <DialogContent
        unstyled
        showCloseButton={false}
        className="add-routing-rule-dialog-content"
      >
        <button
          type="button"
          className="add-routing-rule-close-btn"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label="Close dialog"
        >
          <X
            strokeWidth={ICON_STROKE_WIDTH}
            size={20}
          />
        </button>

        <header className="add-routing-rule-header">
          <h2 className="add-routing-rule-title">
            {isEdit ? 'Edit assignment rule' : 'Add assignment rule'}
          </h2>
          <p className="add-routing-rule-subtitle">
            Auto-assign matching studies to a radiologist or group when they&apos;re booked.
            Subspecialty and expertise are matched against the doctor&apos;s profile.
          </p>
        </header>

        <form
          className="add-routing-rule-form"
          onSubmit={handleSubmit}
        >
          <div className="add-routing-rule-form-body">
            {loadError ? (
              <p
                className="add-routing-rule-error"
                role="alert"
              >
                {loadError}
              </p>
            ) : null}
            {submitError ? (
              <p
                className="add-routing-rule-error"
                role="alert"
              >
                {submitError}
              </p>
            ) : null}

            <div className="add-routing-rule-field">
              <label
                className="add-routing-rule-label"
                htmlFor="assignment-rule-name"
              >
                Rule name
              </label>
              <input
                id="assignment-rule-name"
                type="text"
                className="add-routing-rule-input"
                placeholder="e.g. CT Neuro STAT → Dr. Banerjee"
                value={form.name}
                onChange={e => updateForm('name', e.target.value)}
              />
            </div>

            <div className="add-routing-rule-field">
              <span className="add-routing-rule-label">Match modality</span>
              <div className="add-routing-rule-modality-pills">
                <AssignmentModalityPills
                  loadingModalities={loadingModalities}
                  modalityOptions={modalityOptions}
                  selectedModalityIds={form.modalityIds}
                  onToggle={toggleModality}
                />
              </div>
            </div>

            <div className="add-routing-rule-grid add-routing-rule-grid--two">
              <AssignmentSpecialtyField
                specialtyDropdownRef={specialtyDropdownRef}
                specialtyDropdownOpen={specialtyDropdownOpen}
                onToggleOpen={() => {
                  setSpecialtyDropdownOpen(open => !open);
                  setRadiologistPickerOpen(false);
                }}
                requiredExpertise={form.requiredExpertise}
                specialtyIsAll={specialtyIsAll}
                specialtySearchQuery={specialtySearchQuery}
                onSpecialtySearchChange={setSpecialtySearchQuery}
                showAllSpecialtiesOption={showAllSpecialtiesOption}
                filteredSpecialtyOptions={filteredSpecialtyOptions}
                onSpecialtySelect={handleSpecialtySelect}
              />

              <div className="add-routing-rule-field">
                <label
                  className="add-routing-rule-label"
                  htmlFor="assignment-rule-subspecialty"
                >
                  Study subspecialty <span className="add-routing-rule-optional">(optional)</span>
                </label>
                <RadiologyTestNamePicker
                  id="assignment-rule-subspecialty"
                  selectedTestNames={form.selectedTestNames}
                  onChange={selectedTestNames => updateForm('selectedTestNames', selectedTestNames)}
                  tests={filteredRadiologyTests}
                  loading={loadingRadiologyTests}
                  optional
                  optionalLabel="Any test"
                  placeholder="Select one or more test names…"
                  emptyMessage={
                    filteredRadiologyTests.length === 0 && !loadingRadiologyTests
                      ? 'No tests available'
                      : 'No tests found'
                  }
                />
                <p className="add-routing-rule-hint">
                  Select one or more test names from radiology test master. Use ✕ on a chip or click
                  a selected test again to remove it.
                  {selectedModalityCodes.length ? ' Filtered by selected modalities.' : ''}
                </p>
              </div>
            </div>

            <div className="add-routing-rule-grid add-routing-rule-grid--two">
              <div className="add-routing-rule-field">
                <label
                  className="add-routing-rule-label"
                  htmlFor="assignment-rule-priority"
                >
                  Match priority
                </label>
                <select
                  id="assignment-rule-priority"
                  className="add-routing-rule-select"
                  value={form.matchPriority}
                  onChange={e => updateForm('matchPriority', e.target.value)}
                >
                  {MATCH_PRIORITY_OPTIONS.map(option => (
                    <option
                      key={option.value || 'any'}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="add-routing-rule-field">
              <label
                className="add-routing-rule-label"
                htmlFor="assignment-rule-assign-to"
              >
                Assign to
              </label>
              <select
                id="assignment-rule-assign-to"
                className="add-routing-rule-select"
                value={form.assignmentType}
                onChange={e => {
                  const assignmentType = e.target.value as StudyAssignmentType;
                  setForm(prev => ({
                    ...prev,
                    assignmentType,
                    radiologistId: assignmentType === 'specific' ? prev.radiologistId : null,
                    radiologistIds: assignmentType === 'group' ? prev.radiologistIds : [],
                  }));
                  setRadiologistPickerOpen(false);
                }}
              >
                {ASSIGN_TO_OPTIONS.map(option => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <AssignmentTargetFields
              assignmentType={form.assignmentType}
              radiologistPickerRef={radiologistPickerRef}
              radiologistPickerOpen={radiologistPickerOpen}
              onToggleRadiologistPicker={toggleRadiologistPicker}
              radiologistPickerDisabled={radiologistPickerDisabled}
              selectedSpecificRadiologistName={selectedSpecificRadiologistName}
              selectedGroupRadiologistNames={selectedGroupRadiologistNames}
              radiologistEmptyMessage={radiologistEmptyMessage}
              radiologistSearch={radiologistSearch}
              onRadiologistSearchChange={setRadiologistSearch}
              filteredRadiologists={filteredRadiologists}
              selectedRadiologistId={form.radiologistId}
              selectedRadiologistIds={form.radiologistIds}
              onSpecificSelect={handleSpecificRadiologistSelect}
              onGroupToggle={toggleGroupRadiologist}
              onDone={closeRadiologistPicker}
            />

            <div className="add-routing-rule-field">
              <span className="add-routing-rule-label add-assignment-rule-on-call-label">
                <Clock
                  size={16}
                  strokeWidth={ICON_STROKE_WIDTH}
                  aria-hidden
                />
                On-call window <span className="add-routing-rule-optional">(optional)</span>
              </span>
              <div className="add-assignment-rule-on-call-row">
                <label className="add-assignment-rule-on-call-field">
                  <span className="add-assignment-rule-on-call-field-label">From (0–24)</span>
                  <input
                    type="number"
                    min={0}
                    max={24}
                    step={1}
                    className="add-assignment-rule-on-call-input"
                    value={form.onCallStartHour}
                    onChange={e => updateForm('onCallStartHour', clampHourValue(e.target.value))}
                  />
                </label>
                <span className="add-assignment-rule-on-call-sep">to</span>
                <label className="add-assignment-rule-on-call-field">
                  <span className="add-assignment-rule-on-call-field-label">To (0–24)</span>
                  <input
                    type="number"
                    min={0}
                    max={24}
                    step={1}
                    className="add-assignment-rule-on-call-input"
                    value={form.onCallEndHour}
                    onChange={e => updateForm('onCallEndHour', clampHourValue(e.target.value))}
                  />
                </label>
              </div>
              <p className="add-routing-rule-hint">
                Leave blank to apply at all hours. e.g. From 22 To 6 = active 10 pm – 6 am (crosses
                midnight).
              </p>
            </div>
          </div>

          <footer className="add-routing-rule-footer">
            <div className="add-routing-rule-active">
              <ToggleSwitch
                checked={form.active}
                onChange={checked => updateForm('active', checked)}
              />
              <span>Active</span>
            </div>
            <div className="add-routing-rule-footer-actions">
              <button
                type="button"
                className="add-routing-rule-btn secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="add-routing-rule-btn primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving…' : isEdit ? 'Save rule' : 'Add rule'}
              </button>
            </div>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  );

  return renderAddStudyAssignmentRuleModal();
};

export default AddStudyAssignmentRuleModal;
